import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';
import { 
    SECTION_A_AREAS, 
    SECTION_B_ITEMS, 
    LIKERT_OPTIONS 
} from '../../constants/peopleCareConstants';

/**
 * 📋 PeopleCareModal — Form di Rilevazione del Questionario People Care (IN / OUT)
 * Corrispondente al documento ufficiale V!BRA People Care / Buenavista cooperativa sociale.
 */
const PeopleCareModal = ({ 
    isOpen, 
    onClose, 
    client, 
    counselor, 
    existingAssessment = null, 
    defaultPhase = 'IN',
    onSaved 
}) => {
    const [phase, setPhase] = useState(defaultPhase); // 'IN' | 'OUT'
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [isSaving, setIsSaving] = useState(false);

    // Sezione A (Solo IN)
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [otherAreaText, setOtherAreaText] = useState('');
    const [initialGoalText, setInitialGoalText] = useState('');

    // Sezione B (10 item 1-5 per IN e OUT)
    const [sectionBAnswers, setSectionBAnswers] = useState({
        item1: null,
        item2: null,
        item3: null,
        item4: null,
        item5: null,
        item6: null,
        item7: null,
        item8: null,
        item9: null,
        item10: null
    });

    // Sezione C (Solo OUT)
    const [sectionCAnswers, setSectionCAnswers] = useState({
        goalAchievement: null,
        whatChanged: '',
        mostUseful: '',
        differentApproach: '',
        npsRecommendation: null
    });

    useEffect(() => {
        if (existingAssessment) {
            const isOut = existingAssessment.type === 'people_care_out' || existingAssessment.phase === 'OUT';
            setPhase(isOut ? 'OUT' : 'IN');
            if (existingAssessment.timestamp || existingAssessment.date) {
                const d = new Date(existingAssessment.timestamp || existingAssessment.date);
                if (!isNaN(d.getTime())) setDate(d.toISOString().split('T')[0]);
            }
            if (existingAssessment.sectionA) {
                setSelectedAreas(existingAssessment.sectionA.selectedAreas || []);
                setOtherAreaText(existingAssessment.sectionA.otherAreaText || '');
                setInitialGoalText(existingAssessment.sectionA.initialGoalText || '');
            }
            if (existingAssessment.sectionB) {
                setSectionBAnswers({ ...existingAssessment.sectionB });
            }
            if (existingAssessment.sectionC) {
                setSectionCAnswers({ ...existingAssessment.sectionC });
            }
        } else {
            setPhase(defaultPhase || 'IN');
            setDate(new Date().toISOString().split('T')[0]);
            setSelectedAreas([]);
            setOtherAreaText('');
            setInitialGoalText('');
            setSectionBAnswers({
                item1: null, item2: null, item3: null, item4: null, item5: null,
                item6: null, item7: null, item8: null, item9: null, item10: null
            });
            setSectionCAnswers({
                goalAchievement: null,
                whatChanged: '',
                mostUseful: '',
                differentApproach: '',
                npsRecommendation: null
            });
        }
    }, [existingAssessment, defaultPhase, isOpen]);

    if (!isOpen) return null;

    const toggleArea = (areaId) => {
        setSelectedAreas(prev => 
            prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
        );
    };

    const handleSectionBChange = (itemId, score) => {
        setSectionBAnswers(prev => ({
            ...prev,
            [itemId]: score
        }));
    };

    // Calculate Section B Average
    const answeredBItems = Object.values(sectionBAnswers).filter(v => v !== null && v !== undefined);
    const averageBScore = answeredBItems.length > 0 
        ? (answeredBItems.reduce((a, b) => a + Number(b), 0) / answeredBItems.length).toFixed(2)
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation for Section B (all 10 items required)
        const missingItems = SECTION_B_ITEMS.filter(item => !sectionBAnswers[item.id]);
        if (missingItems.length > 0) {
            alert(`⚠️ Per favore compila tutti i 10 punti della Sezione B. Mancano: ${missingItems.length} risposte.`);
            return;
        }

        if (phase === 'OUT') {
            if (!sectionCAnswers.goalAchievement || !sectionCAnswers.npsRecommendation) {
                alert("⚠️ Nella Sezione C, indica sia il grado di raggiungimento dell'obiettivo che il punteggio di raccomandazione.");
                return;
            }
        }

        try {
            setIsSaving(true);
            const assessmentType = phase === 'IN' ? 'people_care_in' : 'people_care_out';
            const payload = {
                type: assessmentType,
                phase: phase,
                clientId: client.id,
                clientName: client.name || client.email,
                counselorId: counselor?.id || client.counselorId || 'unassigned',
                counselorName: counselor?.name || counselor?.email || 'Counselor',
                organization: client.organization || counselor?.organizationId || null,
                organizationName: client.organizationName || counselor?.organizationName || null,
                managerName: client.managerName || '',
                date: date,
                timestamp: new Date(date).toISOString(),
                sectionA: phase === 'IN' ? {
                    selectedAreas,
                    otherAreaText,
                    initialGoalText
                } : null,
                sectionB: {
                    ...sectionBAnswers,
                    averageScore: Number(averageBScore)
                },
                sectionC: phase === 'OUT' ? {
                    ...sectionCAnswers
                } : null
            };

            if (existingAssessment?.id) {
                await DataManager.updateAssessment(existingAssessment.id, payload);
            } else {
                await DataManager.saveAssessment(client.id, payload);
            }

            alert(`✅ Questionario People Care (${phase}) salvato con successo!`);
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error("Errore salvataggio People Care:", err);
            alert("Errore durante il salvataggio del questionario. Riprova.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full my-8 border border-slate-100 flex flex-col max-h-[92vh] overflow-hidden">
                
                {/* Header with Logos and Title */}
                <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-teal-50/40 to-emerald-50/30">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <span className="text-xl font-black tracking-tight text-slate-800">
                                V!BRA <span className="text-teal-600 font-bold">PEOPLE CARE</span>
                            </span>
                            <span className="text-slate-300">|</span>
                            <span className="text-xs font-bold text-emerald-700 bg-emerald-100/80 px-2.5 py-1 rounded-full">
                                BuenaVista cooperativa sociale
                            </span>
                        </div>
                        <button 
                            onClick={onClose}
                            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 font-bold flex items-center justify-center transition"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                                QUESTIONARIO PEOPLE CARE
                            </h2>
                            <p className="text-xs font-semibold text-teal-700 mt-0.5">
                                Strumento IN / OUT | Fase pilota Buenavista
                            </p>
                        </div>

                        {/* Phase Switcher (IN vs OUT) */}
                        <div className="flex items-center bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
                            <button
                                type="button"
                                onClick={() => setPhase('IN')}
                                className={`px-4 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
                                    phase === 'IN' 
                                        ? 'bg-teal-600 text-white shadow-md shadow-teal-600/20' 
                                        : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                <span>🌱</span> INIZIO PERCORSO (IN)
                            </button>
                            <button
                                type="button"
                                onClick={() => setPhase('OUT')}
                                className={`px-4 py-2 rounded-xl text-xs font-black transition flex items-center gap-1.5 ${
                                    phase === 'OUT' 
                                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20' 
                                        : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                <span>🌳</span> FINE PERCORSO (OUT)
                            </button>
                        </div>
                    </div>
                </div>

                {/* Body Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 bg-slate-50/30">
                    
                    {/* Meta info box */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Partecipante</span>
                            <span className="text-base font-bold text-slate-800">{client.name || client.email}</span>
                            {client.managerName && (
                                <span className="text-xs text-slate-500 block mt-0.5">
                                    Resp. gerarchico: <strong>{client.managerName}</strong>
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Data Rilevazione</label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={e => setDate(e.target.value)}
                                    required
                                    className="p-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Disclaimer box */}
                    <div className="p-4 bg-teal-50/60 rounded-2xl border border-teal-100 text-xs text-teal-900 leading-relaxed">
                        <strong>Finalità:</strong> Questo questionario accompagna il percorso People Care e aiuta la persona a osservare la propria situazione all'inizio e alla fine del percorso. Non è un test diagnostico né uno strumento di valutazione aziendale. Le risposte sono trattate nel rispetto della riservatezza del rapporto di counseling.
                    </div>

                    {/* ========================================================================= */}
                    {/* SEZIONE A: Solo ad Inizio Percorso (IN) */}
                    {/* ========================================================================= */}
                    {phase === 'IN' && (
                        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-6 animate-fade-in">
                            <div className="border-b border-slate-100 pb-3">
                                <span className="text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-50 px-2.5 py-0.5 rounded-full">
                                    Sezione A
                                </span>
                                <h3 className="text-lg font-black text-slate-800 mt-1">
                                    All'inizio del percorso — cosa desidero?
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    In questo momento, quale area senti maggiormente il bisogno di esplorare? (Puoi indicare più di una risposta)
                                </p>
                            </div>

                            {/* Checkbox Aree */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {SECTION_A_AREAS.map(area => {
                                    const isChecked = selectedAreas.includes(area.id);
                                    return (
                                        <div
                                            key={area.id}
                                            onClick={() => toggleArea(area.id)}
                                            className={`p-3.5 rounded-2xl border transition cursor-pointer flex items-center gap-3 select-none ${
                                                isChecked
                                                    ? 'bg-teal-50/80 border-teal-500 shadow-sm ring-1 ring-teal-500'
                                                    : 'bg-slate-50/60 border-slate-200 hover:bg-slate-100'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => {}} // handled by parent div
                                                className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer pointer-events-none"
                                            />
                                            <span className="text-lg">{area.icon}</span>
                                            <span className="text-xs font-bold text-slate-800 flex-1">{area.label}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Campo Altro */}
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">Altro (specificare):</label>
                                <input
                                    type="text"
                                    value={otherAreaText}
                                    onChange={e => setOtherAreaText(e.target.value)}
                                    placeholder="Altra area o tema specifico..."
                                    className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                />
                            </div>

                            {/* Obiettivo Qualitativo */}
                            <div>
                                <label className="block text-xs font-bold text-slate-800 mb-1">
                                    Se dovessi descrivere con poche parole ciò che vorresti ottenere da questo percorso, cosa diresti?
                                </label>
                                <textarea
                                    rows="3"
                                    value={initialGoalText}
                                    onChange={e => setInitialGoalText(e.target.value)}
                                    placeholder="Trascrivi le parole del partecipante durante il colloquio..."
                                    className="w-full p-3.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
                                />
                            </div>
                        </div>
                    )}

                    {/* ========================================================================= */}
                    {/* SEZIONE B: Come mi percepisco oggi (10 Item Likert 1-5, IN & OUT) */}
                    {/* ========================================================================= */}
                    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-6">
                        <div className="border-b border-slate-100 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-50 px-2.5 py-0.5 rounded-full">
                                    Sezione B (Comune a IN e OUT)
                                </span>
                                <h3 className="text-lg font-black text-slate-800 mt-1">
                                    Come mi percepisco oggi
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Per ciascuna affermazione indica quanto ti rappresenta in questo momento:
                                </p>
                            </div>

                            {averageBScore && (
                                <div className="px-4 py-2 bg-teal-50 rounded-2xl border border-teal-200 text-right">
                                    <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider block">Media Sezione B</span>
                                    <span className="text-lg font-black text-teal-900">{averageBScore} / 5.0</span>
                                </div>
                            )}
                        </div>

                        {/* Legenda Scala */}
                        <div className="grid grid-cols-5 gap-1 text-center bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-[11px] font-bold text-slate-600">
                            {LIKERT_OPTIONS.map(opt => (
                                <div key={opt.value} className="px-1">{opt.label}</div>
                            ))}
                        </div>

                        {/* 10 Affermazioni */}
                        <div className="space-y-4">
                            {SECTION_B_ITEMS.map((item, index) => {
                                const currentScore = sectionBAnswers[item.id];
                                return (
                                    <div 
                                        key={item.id}
                                        className={`p-4 rounded-2xl border transition ${
                                            currentScore 
                                                ? 'bg-white border-slate-200 shadow-sm' 
                                                : 'bg-amber-50/40 border-amber-200/60'
                                        }`}
                                    >
                                        <p className="text-xs md:text-sm font-bold text-slate-800 mb-3">
                                            {item.text}
                                        </p>

                                        {/* 5 Radio Buttons */}
                                        <div className="grid grid-cols-5 gap-2">
                                            {LIKERT_OPTIONS.map(opt => {
                                                const isSelected = currentScore === opt.value;
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => handleSectionBChange(item.id, opt.value)}
                                                        className={`py-2.5 px-1 rounded-xl text-xs font-black transition flex flex-col items-center justify-center gap-0.5 ${
                                                            isSelected
                                                                ? 'bg-teal-600 text-white shadow-md shadow-teal-600/20 scale-[1.02]'
                                                                : 'bg-slate-100/80 hover:bg-slate-200 text-slate-700'
                                                        }`}
                                                    >
                                                        <span className="text-sm font-black">{opt.value}</span>
                                                        <span className="text-[9px] font-semibold opacity-80 hidden sm:inline">
                                                            {opt.value === 1 ? 'Per nulla' : opt.value === 5 ? 'Pienamente' : ''}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ========================================================================= */}
                    {/* SEZIONE C: Solo a Fine Percorso (OUT) */}
                    {/* ========================================================================= */}
                    {phase === 'OUT' && (
                        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-6 animate-fade-in">
                            <div className="border-b border-slate-100 pb-3">
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full">
                                    Sezione C (Termine degli 8 incontri)
                                </span>
                                <h3 className="text-lg font-black text-slate-800 mt-1">
                                    Solo a fine percorso — Valutazione ed Esiti
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Compilare questa sezione al termine degli 8 incontri per il follow-up e la restituzione.
                                </p>
                            </div>

                            {/* Domanda 1: Obiettivo Raggiunto */}
                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                                <label className="block text-xs font-bold text-slate-800 mb-2">
                                    1. Quanto senti di aver raggiunto l'obiettivo che avevi individuato all'inizio del percorso?
                                </label>
                                <div className="grid grid-cols-5 gap-2">
                                    {LIKERT_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setSectionCAnswers({ ...sectionCAnswers, goalAchievement: opt.value })}
                                            className={`py-2 px-1 rounded-xl text-xs font-black transition ${
                                                sectionCAnswers.goalAchievement === opt.value
                                                    ? 'bg-emerald-600 text-white shadow-md'
                                                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Domanda 2: Cosa è cambiato */}
                            <div>
                                <label className="block text-xs font-bold text-slate-800 mb-1">
                                    2. Cosa è cambiato maggiormente per te durante questo percorso?
                                </label>
                                <textarea
                                    rows="2"
                                    value={sectionCAnswers.whatChanged}
                                    onChange={e => setSectionCAnswers({ ...sectionCAnswers, whatChanged: e.target.value })}
                                    placeholder="Descrivi le principali evoluzioni o consapevolezze..."
                                    className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                />
                            </div>

                            {/* Domanda 3: Utilità */}
                            <div>
                                <label className="block text-xs font-bold text-slate-800 mb-1">
                                    3. Cosa ti è stato maggiormente utile nel percorso di counseling?
                                </label>
                                <textarea
                                    rows="2"
                                    value={sectionCAnswers.mostUseful}
                                    onChange={e => setSectionCAnswers({ ...sectionCAnswers, mostUseful: e.target.value })}
                                    placeholder="Strumenti, relazione, ascolto, momenti chiave..."
                                    className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                />
                            </div>

                            {/* Domanda 4: Differente approccio */}
                            <div>
                                <label className="block text-xs font-bold text-slate-800 mb-1">
                                    4. C'è qualcosa che oggi senti di poter affrontare diversamente rispetto all'inizio del percorso?
                                </label>
                                <textarea
                                    rows="2"
                                    value={sectionCAnswers.differentApproach}
                                    onChange={e => setSectionCAnswers({ ...sectionCAnswers, differentApproach: e.target.value })}
                                    placeholder="Nuove modalità di risposta, confini, scelte..."
                                    className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                />
                            </div>

                            {/* Domanda 5: Raccomandazione / NPS */}
                            <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-200">
                                <label className="block text-xs font-bold text-emerald-950 mb-2">
                                    5. Quanto consiglieresti People Care a un collega che desiderasse uno spazio di ascolto e sviluppo?
                                </label>
                                <div className="grid grid-cols-5 gap-2">
                                    {LIKERT_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setSectionCAnswers({ ...sectionCAnswers, npsRecommendation: opt.value })}
                                            className={`py-2 px-1 rounded-xl text-xs font-black transition ${
                                                sectionCAnswers.npsRecommendation === opt.value
                                                    ? 'bg-emerald-700 text-white shadow-md'
                                                    : 'bg-white border border-emerald-200 text-slate-700 hover:bg-emerald-100/50'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer / Submit */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-xs font-bold text-slate-400 hover:text-slate-700 transition"
                        >
                            Annulla
                        </button>

                        <Button
                            disabled={isSaving}
                            className="w-full sm:w-auto px-8 py-3.5 bg-teal-600 hover:bg-teal-700 text-white rounded-2xl text-sm font-bold shadow-lg shadow-teal-600/20"
                        >
                            {isSaving ? 'Salvataggio in corso...' : `💾 Salva Questionario People Care (${phase})`}
                        </Button>
                    </div>

                </form>

            </div>
        </div>
    );
};

export default PeopleCareModal;
